import axios from 'axios';

// 공휴일 전용 axios 인스턴스 생성 (외부 API용)
const holidayInstance = axios.create({
    timeout: 5000,
    headers: {
        'Content-Type': 'application/json',
    }
});

// 공공데이터포털 공휴일 API 서비스
class HolidayApiService {
    constructor() {
        this.baseURL = 'https://apis.data.go.kr/B090041/openapi/service/SpcdeInfoService';
        this.serviceKey = 'cb55d366ed174eefef198123580ea29359d4d7ee6f0df52d2fb72e7d42c48f49';
        this.isApiAvailable = true; // API 사용 가능 여부 플래그
    }

    // 기본 공휴일 데이터 (API 실패 시 사용)
    getDefaultHolidays(year) {
        const defaultHolidays = [
            { date: `${year}-01-01`, name: '신정', isHoliday: true },
            { date: `${year}-03-01`, name: '삼일절', isHoliday: true },
            { date: `${year}-05-05`, name: '어린이날', isHoliday: true },
            { date: `${year}-06-06`, name: '현충일', isHoliday: true },
            { date: `${year}-08-15`, name: '광복절', isHoliday: true },
            { date: `${year}-10-03`, name: '개천절', isHoliday: true },
            { date: `${year}-10-09`, name: '한글날', isHoliday: true },
            { date: `${year}-12-25`, name: '성탄절', isHoliday: true }
        ];
        
        return defaultHolidays;
    }

    // 특정 연도의 공휴일 정보 조회
    async getHolidays(year) {
        // API가 사용 불가능한 경우 기본 공휴일 반환
        if (!this.isApiAvailable) {
            console.log('공휴일 API 사용 불가, 기본 공휴일 사용');
            return this.getDefaultHolidays(year);
        }

        try {
            const response = await holidayInstance.get(`${this.baseURL}/getRestDeInfo`, {
                params: {
                    ServiceKey: this.serviceKey,
                    solYear: year,
                    numOfRows: 100,
                    pageNo: 1,
                    _type: 'json'
                }
            });
            
            if (response.data.response?.header?.resultCode === '00') {
                const holidays = response.data.response.body?.items?.item || [];
                
                // 배열이 아닌 경우 배열로 변환
                const holidayArray = Array.isArray(holidays) ? holidays : [holidays];
                
                // 날짜 형식을 YYYY-MM-DD로 변환하여 반환
                const formattedHolidays = holidayArray.map(holiday => {
                    // API 응답에서 날짜 필드명 확인 (locdate 또는 다른 필드명일 수 있음)
                    const dateValue = holiday.locdate || holiday.date || holiday.solDate || holiday.lunDate;
                    const formattedDate = this.formatDate(dateValue);
                    
                    return {
                        date: formattedDate,
                        name: holiday.dateName || holiday.name,
                        isHoliday: holiday.isHoliday === 'Y' || holiday.isHoliday === true || holiday.isHoliday === 'true'
                    };
                });
                
                return formattedHolidays;
            } else {
                console.warn('API 응답 오류, 기본 공휴일 사용:', response.data.response?.header);
                this.isApiAvailable = false; // API 사용 불가능으로 설정
                return this.getDefaultHolidays(year);
            }
        } catch (error) {
            console.warn('공휴일 API 호출 실패, 기본 공휴일 사용:', error.message);
            this.isApiAvailable = false; // API 사용 불가능으로 설정
            return this.getDefaultHolidays(year);
        }
    }

    // 날짜 형식 변환 (YYYYMMDD -> YYYY-MM-DD)
    formatDate(dateString) {
        if (!dateString) {
            return null;
        }
        
        // 숫자인 경우 문자열로 변환
        const dateStr = String(dateString);
        
        if (dateStr.length !== 8) {
            return null;
        }
        
        const year = dateStr.substring(0, 4);
        const month = dateStr.substring(4, 6);
        const day = dateStr.substring(6, 8);
        
        const formattedDate = `${year}-${month}-${day}`;
        
        return formattedDate;
    }

    // 특정 날짜가 공휴일인지 확인
    isHoliday(date, holidays) {
        const dateString = date.format('YYYY-MM-DD');
        const result = holidays.some(holiday => holiday.date === dateString && holiday.isHoliday);
        
        return result;
    }

    // 여러 연도의 공휴일 정보를 한번에 가져오기
    async getHolidaysForYears(years) {
        try {
            const promises = years.map(year => this.getHolidays(year));
            const results = await Promise.all(promises);
            
            const flatResults = results.flat();
            
            return flatResults;
        } catch (error) {
            console.warn('여러 연도 공휴일 조회 실패, 기본 공휴일 사용:', error);
            // 모든 연도에 대해 기본 공휴일 반환
            const defaultHolidays = [];
            years.forEach(year => {
                defaultHolidays.push(...this.getDefaultHolidays(year));
            });
            return defaultHolidays;
        }
    }

    // API 사용 가능 여부 확인
    checkApiAvailability() {
        return this.isApiAvailable;
    }

    // API 사용 가능 여부 재설정 (필요시 사용)
    resetApiAvailability() {
        this.isApiAvailable = true;
    }
}

const holidayApiInstance = new HolidayApiService();
export default holidayApiInstance;